/** Shared servicing amounts: Closing Disclosure payment, never housing expense. */
import { paymentCents, paymentFieldsFromRow, resolveGoodbyePayment } from './goodbye-letter-payment.ts';
import { GoodbyeLetterPrereqError } from './goodbye-letter-types.ts';

// ---------------------------------------------------------------------------
// Monthly PI
// ---------------------------------------------------------------------------

/**
 * Standard amortization: M = P * [r(1+r)^n] / [(1+r)^n - 1]
 */
export function calculateMonthlyPI(
  loanAmount: number,
  annualRate: number,
  termMonths: number,
): number {
  if (termMonths <= 0) return 0;
  const monthlyRate = annualRate / 100 / 12;
  if (monthlyRate === 0) return loanAmount / termMonths;
  const factor = Math.pow(1 + monthlyRate, termMonths);
  return loanAmount * (monthlyRate * factor) / (factor - 1);
}

// ---------------------------------------------------------------------------
// Escrow computation from Encompass fields
// ---------------------------------------------------------------------------

/** Validate the disclosed payment for the specific installment before allocating it. */
export function computeMonthlyAmounts(
  el: Record<string, unknown>,
  firstPaymentDate: string,
  dueDate: string = firstPaymentDate
) {
  const evidence = resolveGoodbyePayment(paymentFieldsFromRow(el), firstPaymentDate, dueDate);
  const principalCents = paymentCents(el.rate_lock_base_loan_amount);
  const rate = el.trans_details_interest_rate;
  const interestRate = typeof rate === 'number' || (typeof rate === 'string' && rate.trim() !== '')
    ? Number(rate) : NaN;
  const termMonths = Number(el.loan_term_months);
  if (principalCents === null || principalCents <= 0 || !Number.isFinite(interestRate) || interestRate < 0 ||
    !Number.isInteger(termMonths) || termMonths <= 0) {
    throw new GoodbyeLetterPrereqError('PAYMENT_DATA_INCOMPLETE',
      'The loan balance, interest rate or term is missing or invalid. Review Encompass before creating a payment schedule.',
      ['rate_lock_base_loan_amount', 'trans_details_interest_rate', 'loan_term_months']);
  }
  return {
    loanAmount: principalCents / 100,
    interestRate,
    termMonths,
    monthlyPI: evidence.principalAndInterestCents / 100,
    monthlyEscrow: evidence.collectedEscrowCents / 100,
    paymentEvidence: evidence
  };
}

// ---------------------------------------------------------------------------
// Single-payment amortization entry
// ---------------------------------------------------------------------------

export interface PaymentEntry {
  payment_number: number;
  due_date: string;
  principal_due: number;
  interest_due: number;
  escrow_due: number;
  total_amount_due: number;
  interest_rate: number;
}

/**
 * Generate a single amortization payment entry.
 * Uses interest-first rounding to match Encompass convention.
 */
export function generateSinglePayment(
  remainingBalance: number,
  annualRate: number,
  monthlyPI: number,
  monthlyEscrow: number,
  dueDate: string,
  paymentNumber: number,
): PaymentEntry {
  const monthlyRate = annualRate / 100 / 12;

  let interestDue = Math.round(remainingBalance * monthlyRate * 100) / 100;
  let principalDue = Math.round((monthlyPI - interestDue) * 100) / 100;

  // Snap near-zero principal (floating-point artifact on IO loans)
  if (Math.abs(principalDue) < 0.015) {
    principalDue = 0;
    interestDue = Math.round(monthlyPI * 100) / 100;
  }

  const totalDue = Math.round((monthlyPI + monthlyEscrow) * 100) / 100;

  return {
    payment_number: paymentNumber,
    due_date: dueDate,
    principal_due: principalDue,
    interest_due: interestDue,
    escrow_due: Math.round(monthlyEscrow * 100) / 100,
    total_amount_due: totalDue,
    interest_rate: annualRate,
  };
}

/**
 * Compute the next monthly due date from a previous due date.
 * Advances by one calendar month (same day-of-month).
 */
export function nextMonthlyDueDate(previousDueDate: string): string {
  const d = new Date(previousDueDate);
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().split('T')[0];
}
